<template>
    <div class="q-carousel">
        <div class="q-carousel-track" :style="trackStyle" @transitionend="onTransitionEnd">
            <div v-for="(item, i) in windowItems" :key="i" class="q-carousel-slide">
                <slot :item="item" :index="i" />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { usePageShowHide } from '../../hooks/usePageShowHide';

const props = withDefaults(
    defineProps<{
        list: any[];
        stay?: number;
    }>(),
    {
        list: () => [],
        stay: 4,
    }
);

// 当前展示项在原始列表中的真实索引
const realIndex = ref(0);

// 只渲染 3 个 slide：[上一个, 当前, 下一个]，与列表长度无关
const windowItems = computed(() => {
    const len = props.list.length;
    if (len === 0) return [];
    if (len === 1) return [props.list[0]];
    const prev = (realIndex.value - 1 + len) % len;
    const curr = realIndex.value;
    const next = (realIndex.value + 1) % len;
    return [props.list[prev], props.list[curr], props.list[next]];
});

// trackIndex 始终在 1（中间）处于静止，过渡时移向 2（下一个）
const trackIndex = ref(1);
const animated = ref(false);

const trackStyle = computed(() => ({
    transform: `translateY(-${trackIndex.value * 100}%)`,
    transition: animated.value ? 'transform 0.5s ease-in-out' : 'none',
}));

let timer: ReturnType<typeof setTimeout> | null = null;

const clearTimer = () => {
    if (timer !== null) {
        clearTimeout(timer);
        timer = null;
    }
};

const scheduleNext = () => {
    clearTimer();
    timer = setTimeout(() => {
        animated.value = true;
        trackIndex.value = 2;
    }, props.stay * 1000);
};

const onTransitionEnd = () => {
    // 1. 更新真实索引到下一项
    realIndex.value = (realIndex.value + 1) % props.list.length;
    // 2. 关闭过渡，瞬间重置回中间位置
    animated.value = false;
    trackIndex.value = 1;
    // 3. DOM 更新完成后重新计时
    nextTick(() => {
        scheduleNext();
    });
};

// 页面隐藏时停止轮播，回显时恢复
const pause = () => {
    clearTimer();
    // 若正在过渡中，立即停在当前位置，避免残留动画
    if (animated.value) {
        animated.value = false;
        trackIndex.value = 1;
    }
};

const resume = () => {
    scheduleNext();
};

usePageShowHide({ onPageHide: pause, onPageShow: resume, });

onMounted(() => {
    scheduleNext();
});

onUnmounted(() => {
    clearTimer();
});
</script>

<style lang="scss" scoped>
.q-carousel {
    width: 100%;
    height: 100%;
    overflow: hidden;

    &-track {
        height: 100%;
        will-change: transform;
    }

    &-slide {
        height: 100%;
    }
}
</style>
